import api from "./api";

export type LegalDocumentType =
  | "TERMS_AND_CONDITIONS"
  | "PRIVACY_POLICY"
  | "MEDICAL_DISCLAIMER";

export interface LegalDocument {
  id: string;
  documentType: LegalDocumentType;
  version: string | number;
  changeType?: "MAJOR" | "MINOR" | "PATCH" | null;
  title: string;
  content: string;
  isMandatory: boolean;
  requiresReAcceptance: boolean;
  effectiveFrom: string | null;
  publishedAt: string | null;
}

export interface LegalAcceptanceRecord {
  id: string;
  userId: number;
  legalDocumentId: string;
  documentType: LegalDocumentType;
  version: string | number;
  action: "ACCEPTED" | "ACKNOWLEDGED";
  acceptedAt: string;
  createdAt: string;
}

export interface RequiredLegalDocumentsResponse {
  requiresAcceptance: boolean;
  requiredDocuments: LegalDocument[];
  documents?: LegalDocument[];
}

/**
 * Fetch all currently published legal documents.
 */
export const getPublishedLegalDocuments = async (): Promise<LegalDocument[]> => {
  const response = await api.get("/legal-documents");
  return response.data?.data || [];
};

/**
 * Fetch currently published legal document for a specific type.
 */
export const getPublishedLegalDocumentByType = async (
  type: LegalDocumentType | string
): Promise<LegalDocument> => {
  const response = await api.get(`/legal-documents/${type}`);
  return response.data?.data;
};

/**
 * Check if the authenticated user has pending mandatory legal documents.
 */
export const getRequiredLegalDocuments = async (): Promise<RequiredLegalDocumentsResponse> => {
  const response = await api.get("/legal-documents/required");
  const data = response.data?.data;
  return {
    requiresAcceptance: Boolean(data?.requiresAcceptance),
    requiredDocuments: data?.requiredDocuments || data?.documents || [],
  };
};

/**
 * Fetch authenticated user's own legal acceptance history.
 */
export const getMyAcceptanceHistory = async (): Promise<LegalAcceptanceRecord[]> => {
  const response = await api.get("/legal-acceptance/me");
  return response.data?.data || [];
};

/**
 * Record user acceptance or acknowledgment for a published document.
 * Note: Never sends userId; authenticated user is resolved server-side.
 */
export const recordLegalAcceptance = async (payload: {
  legalDocumentId: string;
  documentType?: string;
  version?: string | number;
  action?: "ACCEPTED" | "ACKNOWLEDGED";
}): Promise<LegalAcceptanceRecord> => {
  const response = await api.post("/legal-acceptance", payload);
  return response.data?.data;
};
